import { stripHtmlTags } from "@/helpers/strip-html-tags";
import { useMemo } from "react";

type UseServiceContentProps =
  | {
      seoHeading?: string;
      heading?: string;
      subHeading?: string;
      description: string;
      image?: string;
      hasHtmlDescription: true;
    }
  | {
      hasHtmlDescription: false;
      list: {
        seoHeading?: string;
        heading?: string;
        subHeading?: string;
        description: string;
        image?: string;
      }[];
    };

type ServiceContentsReturnType = {
  seoHeading?: string;
  heading: string;
  subHeading: string;
  description?: string;
  image?: string;
  list: string[];
};

export const useServiceContent = (
  content: UseServiceContentProps,
  asRow: boolean = false
): ServiceContentsReturnType => {
  const serviceContents = useMemo<ServiceContentsReturnType>(() => {
    if (!content) {
      return {
        seoHeading: "",
        heading: "",
        subHeading: "",
        image: "",
        list: [],
      };
    }

    if (content.hasHtmlDescription) {
      const hasUl = /<ul[^>]*>/i.test(content.description);
      return {
        seoHeading: content.seoHeading ?? "",
        heading: content.heading ?? "",
        subHeading: content.subHeading ?? "",
        image: content.image ?? "",
        description: asRow
          ? wrapInDivs(content.description)
          : !hasUl
            ? content.description
            : undefined,
        list: unOrderedListToArray(content.description),
      };
    } else {
      const firstItem = (content?.list?.length && content.list[0]) || {
        seoHeading: "",
        heading: "",
        subHeading: "",
        image: "",
      };
      return {
        seoHeading: firstItem?.seoHeading ?? "",
        heading: firstItem?.heading ?? "",
        subHeading: firstItem.subHeading ?? "",
        image: firstItem.image ?? "",
        list: content?.list?.map((item) => item.description),
      };
    }
  }, [content, asRow]);

  return serviceContents;
};

export const wrapInDivs = (str: string): string => {
  if (!str) return "";
  // Wraps <h3> and the immediate following <ul> inside <div class="exam-format">
  return str.replace(
    /(<h3[^>]*>[\s\S]*?<\/h3>)(?:\s*<ul[^>]*>[\s\S]*?<\/ul>)?/gi,
    '<div class="exam-format">$&</div>'
  );
};

export const unOrderedListToArray = (str: string): string[] => {
  if (!str) return [];
  const items: string[] = [];
  const ulRegex = /<ul[^>]*>([\s\S]*?)<\/ul>/gi;
  let ulMatch;
  while ((ulMatch = ulRegex.exec(str)) !== null) {
    const ulContent = ulMatch[1];
    const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
    let liMatch;
    while (ulContent && (liMatch = liRegex.exec(ulContent)) !== null) {
      items.push(stripHtmlTags(liMatch[1] || ""));
    }
  }
  return items;
};
